import { useState, useCallback, useRef } from 'react';
import { Upload, Download, Loader2, FileText, X, Plus } from 'lucide-react';
import { PDFDocument } from 'pdf-lib';

const ACCEPT_PDF = 'application/pdf';

/** 無法處理的 PDF 時顯示的建議訊息 */
const MSG_UNSUPPORTED_PDF =
  '此 PDF 可能受保護或格式特殊，無法在瀏覽器內處理。若已用 Chrome／Edge「另存新檔」仍失敗，可改試「列印 → 另存為 PDF」再上傳，或使用其他離線 PDF 軟體（如 Adobe Acrobat）處理。';

/** 若為 pdf-lib 解析/結構錯誤則回傳友善訊息，否則回傳原始訊息 */
function toUserMessage(e: unknown, fallback: string): string {
  const msg = e instanceof Error ? e.message : String(e);
  if (/PDFDict|instance of undefined/i.test(msg)) return MSG_UNSUPPORTED_PDF;
  return msg || fallback;
}

/**
 * 載入 PDF：先一般載入，若為加密再以 ignoreEncryption 重試。
 * 若出現 PDFDict/undefined 等解析錯誤則拋出友善訊息。
 */
async function loadPdfDoc(
  bytes: ArrayBuffer
): Promise<Awaited<ReturnType<typeof PDFDocument.load>>> {
  let lastErr: unknown;
  for (const options of [{}, { ignoreEncryption: true }]) {
    try {
      return await PDFDocument.load(
        bytes,
        options as { ignoreEncryption?: boolean }
      );
    } catch (e) {
      lastErr = e;
      const msg = e instanceof Error ? e.message : String(e);
      if (options.ignoreEncryption === undefined && /encrypted/i.test(msg))
        continue;
      if (/PDFDict|instance of undefined/i.test(msg)) {
        throw new Error(MSG_UNSUPPORTED_PDF);
      }
      throw e;
    }
  }
  throw lastErr;
}

type PdfAction = 'merge' | 'split' | 'compress' | null;

/** 一組頁碼範圍（起始頁、結束頁，1-based；空字串表示未填） */
type PageRangeItem = { start: string; end: string };

/**
 * 將「起始/結束頁」項目列表轉成 0-based 頁碼陣列（每組一檔）。
 * 若某項結束頁未填且下一項有起始頁，則自動視為下一項起始頁 -1。
 */
function pageRangeItemsToGroups(
  items: PageRangeItem[],
  totalPages: number
): number[][] {
  const groups: number[][] = [];
  const parsed = items.map(item => ({
    start: item.start.trim() ? parseInt(item.start.trim(), 10) : NaN,
    end: item.end.trim() ? parseInt(item.end.trim(), 10) : NaN,
  }));

  for (let i = 0; i < parsed.length; i++) {
    const start = parsed[i].start;
    if (Number.isNaN(start) || start < 1) continue;
    let end: number;
    if (!Number.isNaN(parsed[i].end)) {
      end = parsed[i].end;
    } else if (i + 1 < parsed.length && !Number.isNaN(parsed[i + 1].start)) {
      end = parsed[i + 1].start - 1;
    } else {
      end = totalPages;
    }
    const low = Math.max(1, Math.min(start, end));
    const high = Math.min(totalPages, Math.max(start, end));
    const indices: number[] = [];
    for (let p = low; p <= high; p++) indices.push(p - 1);
    if (indices.length) groups.push(indices);
  }
  return groups;
}

/**
 * PDF 轉檔與壓縮：合併、分割、壓縮（介面乾淨無廣告，瀏覽器內完成）
 */
export function PdfToolsTool() {
  const [action, setAction] = useState<PdfAction>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultBlobs, setResultBlobs] = useState<Blob[]>([]);
  const [resultBlobUrls, setResultBlobUrls] = useState<string[]>([]);
  const [downloadZipLoading, setDownloadZipLoading] = useState(false);
  const [splitMode, setSplitMode] = useState<'everyN' | 'range'>('everyN');
  const [splitEveryN, setSplitEveryN] = useState(2);
  const [splitRanges, setSplitRanges] = useState<PageRangeItem[]>([
    { start: '', end: '' },
  ]);
  const inputRef = useRef<HTMLInputElement>(null);

  const isMerge = action === 'merge';
  const isSplit = action === 'split';
  const isCompress = action === 'compress';
  const needMultiple = isMerge;
  const needSingle = isSplit || isCompress;
  const canProcess =
    (needMultiple && files.length >= 2) || (needSingle && files.length >= 1);

  const applyFiles = useCallback((newFiles: File | File[] | null) => {
    setError(null);
    setResultUrl(null);
    setResultBlobs([]);
    if (!newFiles) {
      setFiles([]);
      return;
    }
    const list = Array.isArray(newFiles) ? newFiles : [newFiles];
    const pdfs = list.filter(
      f => f.type === ACCEPT_PDF || f.name.toLowerCase().endsWith('.pdf')
    );
    if (list.length !== pdfs.length) {
      setError('請只選擇 PDF 檔案');
    }
    setFiles(pdfs);
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const chosen = e.target.files;
      if (!chosen?.length) return;
      if (needMultiple) {
        applyFiles(Array.from(chosen));
      } else {
        applyFiles(chosen[0]);
      }
      e.target.value = '';
    },
    [needMultiple, applyFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const dropped = e.dataTransfer.files;
      if (!dropped?.length) return;
      if (needMultiple) {
        applyFiles(Array.from(dropped));
      } else {
        applyFiles(dropped[0]);
      }
    },
    [needMultiple, applyFiles]
  );

  const removeFile = useCallback((index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setError(null);
  }, []);

  /** 新增一組頁碼：先依「下一項起始頁 -1」自動帶入所有未填的結束頁，再追加一列 */
  const addSplitRange = useCallback(() => {
    setSplitRanges(prev => {
      let next = prev.map((item, i) => {
        if ((item.end?.trim() ?? '').length > 0) return item;
        const nextStart = prev[i + 1]?.start?.trim() ?? '';
        const num = parseInt(nextStart, 10);
        if (Number.isNaN(num) || num < 1) return item;
        return { ...item, end: String(num - 1) };
      });
      next = [...next, { start: '', end: '' }];
      return next;
    });
  }, []);

  const removeSplitRange = useCallback((index: number) => {
    setSplitRanges(prev => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [{ start: '', end: '' }];
    });
  }, []);

  const updateSplitRange = useCallback(
    (index: number, field: 'start' | 'end', value: string) => {
      setSplitRanges(prev =>
        prev.map((item, i) =>
          i !== index ? item : { ...item, [field]: value }
        )
      );
    },
    []
  );

  /** 起始頁失焦時：若前一項結束頁為空，則帶入「本項起始頁 - 1」（避免輸入過程中用不完整數字） */
  const fillPrevEndOnStartBlur = useCallback((index: number) => {
    if (index < 1) return;
    setSplitRanges(prev => {
      const currentStart = prev[index]?.start?.trim() ?? '';
      const num = parseInt(currentStart, 10);
      if (
        Number.isNaN(num) ||
        num < 1 ||
        (prev[index - 1]?.end?.trim() ?? '').length > 0
      )
        return prev;
      return prev.map((item, i) =>
        i === index - 1 ? { ...item, end: String(num - 1) } : item
      );
    });
  }, []);

  const clearResult = useCallback(() => {
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    resultBlobUrls.forEach(u => URL.revokeObjectURL(u));
    setResultUrl(null);
    setResultBlobs([]);
    setResultBlobUrls([]);
  }, [resultUrl, resultBlobUrls]);

  /** 將所有分割檔打包成 ZIP 一次下載 */
  const handleDownloadAllAsZip = useCallback(async () => {
    if (resultBlobs.length === 0) return;
    setDownloadZipLoading(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      resultBlobs.forEach((blob, i) => {
        zip.file(`split-${i + 1}.pdf`, blob);
      });
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '分割結果.zip';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadZipLoading(false);
    }
  }, [resultBlobs]);

  const runMerge = useCallback(async () => {
    if (files.length < 2) return;
    setProcessing(true);
    setError(null);
    clearResult();
    try {
      const merged = await PDFDocument.create();
      for (const file of files) {
        const bytes = await file.arrayBuffer();
        const src = await loadPdfDoc(bytes);
        const pages = await merged.copyPages(src, src.getPageIndices());
        for (const page of pages) merged.addPage(page);
      }
      const pdfBytes = await merged.save();
      const blob = new Blob([pdfBytes as BlobPart], { type: ACCEPT_PDF });
      setResultUrl(URL.createObjectURL(blob));
    } catch (e) {
      setError(toUserMessage(e, '合併失敗'));
    } finally {
      setProcessing(false);
    }
  }, [files, clearResult]);

  const runSplit = useCallback(async () => {
    const file = files[0];
    if (!file) return;
    setProcessing(true);
    setError(null);
    clearResult();
    try {
      const bytes = await file.arrayBuffer();
      const src = await loadPdfDoc(bytes);
      const totalPages = src.getPageCount();
      let groups: number[][];

      if (splitMode === 'everyN') {
        const n = Math.max(1, splitEveryN);
        groups = [];
        for (let start = 0; start < totalPages; start += n) {
          const chunk: number[] = [];
          for (let i = start; i < Math.min(start + n, totalPages); i++) {
            chunk.push(i);
          }
          groups.push(chunk);
        }
      } else {
        groups = pageRangeItemsToGroups(splitRanges, totalPages);
        if (groups.length === 0) {
          setError('請至少填寫一組起始頁碼');
          setProcessing(false);
          return;
        }
      }

      const blobs: Blob[] = [];
      for (const indices of groups) {
        const doc = await PDFDocument.create();
        const copied = await doc.copyPages(src, indices);
        for (const page of copied) doc.addPage(page);
        const pdfBytes = await doc.save();
        blobs.push(new Blob([pdfBytes as BlobPart], { type: ACCEPT_PDF }));
      }
      setResultBlobUrls(prev => {
        prev.forEach(u => URL.revokeObjectURL(u));
        return [];
      });
      setResultBlobs(blobs);
      setResultBlobUrls(blobs.map(b => URL.createObjectURL(b)));
    } catch (e) {
      setError(toUserMessage(e, '分割失敗'));
    } finally {
      setProcessing(false);
    }
  }, [files, splitMode, splitEveryN, splitRanges, clearResult]);

  const runCompress = useCallback(async () => {
    const file = files[0];
    if (!file) return;
    setProcessing(true);
    setError(null);
    clearResult();
    try {
      const bytes = await file.arrayBuffer();
      const src = await loadPdfDoc(bytes);
      const indices = src.getPageIndices();
      const doc = await PDFDocument.create();
      const copied = await doc.copyPages(src, indices);
      for (const page of copied) doc.addPage(page);
      const pdfBytes = await doc.save();
      const blob = new Blob([pdfBytes as BlobPart], { type: ACCEPT_PDF });
      setResultUrl(URL.createObjectURL(blob));
    } catch (e) {
      setError(toUserMessage(e, '壓縮失敗'));
    } finally {
      setProcessing(false);
    }
  }, [files, clearResult]);

  const handleProcess = useCallback(() => {
    if (isMerge) runMerge();
    else if (isSplit) runSplit();
    else if (isCompress) runCompress();
  }, [isMerge, isSplit, isCompress, runMerge, runSplit, runCompress]);

  return (
    <>
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-4 text-sm font-medium text-gray-700 dark:text-gray-300">
          選擇功能
        </h2>
        <div className="flex flex-wrap gap-3">
          {[
            { id: 'merge' as const, label: '合併 PDF' },
            { id: 'split' as const, label: '分割 PDF' },
            { id: 'compress' as const, label: '壓縮 PDF' },
          ].map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                setAction(id);
                setFiles([]);
                setError(null);
                clearResult();
              }}
              className={`rounded-lg border px-4 py-3 text-sm font-medium transition ${
                action === id
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {action && (
          <div className="mt-6">
            <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
              {isMerge && '上傳多個 PDF 後可合併為單一檔案。'}
              {isSplit && '上傳 PDF 後可依頁碼或每 N 頁分割。'}
              {isCompress &&
                '上傳 PDF 後可重新打包（可能略為減小體積），全部在瀏覽器內完成。'}
            </p>

            <div
              className={`rounded-lg border-2 border-dashed px-4 py-6 transition-colors ${
                isDragging
                  ? 'border-primary bg-primary/5 dark:border-primary/10'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <label className="inline-flex cursor-pointer items-center gap-2 rounded bg-primary px-4 py-2 text-white hover:bg-primary/90">
                <Upload className="h-4 w-4" />
                <span>選擇 PDF 檔案</span>
                <input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPT_PDF}
                  multiple={needMultiple}
                  onChange={handleFileChange}
                  className="hidden"
                  aria-label="選擇 PDF"
                />
              </label>
              <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
                或拖曳 PDF 到這裡
                {needMultiple && '（可多選）'}
              </span>

              {files.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {files.map((f, i) => (
                    <li
                      key={`${f.name}-${i}`}
                      className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
                    >
                      <FileText className="h-4 w-4 shrink-0 text-gray-500" />
                      <span className="min-w-0 truncate">{f.name}</span>
                      <span className="text-gray-500">
                        ({(f.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        className="shrink-0 rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-700 dark:hover:bg-gray-600 dark:hover:text-gray-300"
                        aria-label="移除"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {isSplit && (
              <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50/50 p-4 dark:border-gray-600 dark:bg-gray-800/50">
                <h3 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  分割方式
                </h3>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="splitMode"
                      checked={splitMode === 'everyN'}
                      onChange={() => setSplitMode('everyN')}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">每</span>
                    <input
                      type="number"
                      min={1}
                      value={splitEveryN}
                      onChange={e =>
                        setSplitEveryN(
                          Math.max(1, parseInt(e.target.value, 10) || 1)
                        )
                      }
                      className="w-16 rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    />
                    <span className="text-sm">頁一檔</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="splitMode"
                      checked={splitMode === 'range'}
                      onChange={() => setSplitMode('range')}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">依頁碼</span>
                  </label>
                </div>
                {splitMode === 'range' && (
                  <div className="mt-3 space-y-2">
                    {splitRanges.map((item, index) => (
                      <div
                        key={index}
                        className="flex flex-wrap items-center gap-2"
                      >
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          第 {index + 1} 檔
                        </span>
                        <input
                          type="number"
                          min={1}
                          placeholder="起始頁"
                          value={item.start}
                          onChange={e =>
                            updateSplitRange(index, 'start', e.target.value)
                          }
                          onBlur={() => fillPrevEndOnStartBlur(index)}
                          className="w-24 rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                          aria-label={`第 ${index + 1} 檔起始頁`}
                        />
                        <span className="text-sm text-gray-500">～</span>
                        <input
                          type="number"
                          min={1}
                          placeholder="結束頁"
                          value={item.end}
                          onChange={e =>
                            updateSplitRange(index, 'end', e.target.value)
                          }
                          className="w-24 rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                          aria-label={`第 ${index + 1} 檔結束頁`}
                        />
                        {splitRanges.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeSplitRange(index)}
                            className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700 dark:hover:bg-gray-600 dark:hover:text-gray-300"
                            aria-label="移除此項"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addSplitRange}
                      className="inline-flex items-center gap-1 rounded border border-dashed border-gray-400 px-3 py-1.5 text-sm text-gray-600 hover:border-primary hover:text-primary dark:border-gray-500 dark:text-gray-400 dark:hover:border-primary dark:hover:text-primary"
                    >
                      <Plus className="h-4 w-4" />
                      新增一組頁碼
                    </button>
                  </div>
                )}
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  依頁碼：每項為「起始頁～結束頁」一檔。若某項結束頁留空，會自動帶入下一項起始頁
                  -1；最後一項結束頁留空則到檔案末頁。
                </p>
              </div>
            )}

            {error && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={!canProcess || processing}
                onClick={handleProcess}
                className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-white hover:bg-primary/90 disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    處理中…
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    {isMerge && '合併並下載'}
                    {isSplit && '分割並下載'}
                    {isCompress && '壓縮並下載'}
                  </>
                )}
              </button>
              {!canProcess && !processing && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {isMerge && '請上傳至少 2 個 PDF'}
                  {isSplit && '請先上傳要分割的 PDF'}
                  {isCompress && '請先上傳要壓縮的 PDF'}
                </span>
              )}
            </div>

            {(resultUrl || resultBlobs.length > 0) && !processing && (
              <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800">
                <h3 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  下載結果
                </h3>
                {resultUrl && (
                  <a
                    href={resultUrl}
                    download={
                      isMerge
                        ? 'merged.pdf'
                        : isCompress
                          ? 'compressed.pdf'
                          : 'output.pdf'
                    }
                    className="inline-flex items-center gap-2 rounded bg-gray-700 px-3 py-2 text-sm text-white hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-500"
                  >
                    <Download className="h-4 w-4" />
                    下載 PDF
                  </a>
                )}
                {resultBlobUrls.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleDownloadAllAsZip}
                      disabled={downloadZipLoading}
                      className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-sm text-white hover:bg-primary/90 disabled:opacity-50"
                    >
                      {downloadZipLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                      一次下載 (ZIP)
                    </button>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      或逐檔：
                    </span>
                    {resultBlobUrls.map((url, i) => (
                      <a
                        key={i}
                        href={url}
                        download={`split-${i + 1}.pdf`}
                        className="inline-flex items-center gap-2 rounded bg-gray-700 px-3 py-2 text-sm text-white hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-500"
                      >
                        <Download className="h-4 w-4" />第 {i + 1} 檔
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          特色
        </h2>
        <ul className="list-inside list-disc space-y-1 text-sm text-gray-600 dark:text-gray-400">
          <li>合併：多檔合為一檔</li>
          <li>分割：依頁碼或固定頁數</li>
          <li>壓縮：重新打包，可能略為減小體積</li>
          <li>全部在瀏覽器完成，隱私安全</li>
        </ul>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
          使用說明
        </h2>
        <p className="leading-relaxed text-gray-600 dark:text-gray-400">
          提供 PDF
          合併、分割與壓縮，介面簡潔無廣告。處理於本機完成，不將檔案上傳至伺服器，兼顧速度與隱私。
        </p>
      </section>
    </>
  );
}
